import { h, Fragment } from "preact";

import Link, { LinkProps } from "../../../foundation/router/Link";
import { isHalloween } from "../../../foundation/utils/celebration";
import classname from "../../../foundation/utils/classname";
import IconCheck from "../Icons/IconCheck";
import IconCloseSmall from "../Icons/IconCloseSmall";
import ResponsiveImage from "../../ResponsiveImage";
import CircleLoader from "../CircleLoader";

import styles from "./Teyze.module.css";

enum Variant {
    Informative = "informative",
    Warning = "warning",
    Onboarding = "onboarding"
}

enum ImageSize {
    Small = "small",
    Medium = "medium"
}

enum ActionStatus {
    Default = "default",
    Loading = "loading",
    Completed = "completed"
}

interface TeyzeLinkProps extends LinkProps {
    label: string;
}

interface Props {
    class?: string;
    text?: string | JSX.Element;
    textAfterInnerLink?: string | JSX.Element;
    variant?: Variant;
    link?: TeyzeLinkProps;
    innerLink?: TeyzeLinkProps;
    subText?: JSX.Element;
    imageSize?: ImageSize;
    ninja?: boolean;
    actionStatus?: ActionStatus;
    onClose?: () => void;
    onLinkClick?: (event: MouseEvent) => void;
}

const TEYZE_VARIANT_IMAGES = {
    [Variant.Informative]: "teyzeInformative",
    [Variant.Warning]: "teyzeWarning",
    [Variant.Onboarding]: "teyzeOnboarding"
};

function Teyze({
    class: cssClass,
    text,
    textAfterInnerLink = "",
    variant = Variant.Informative,
    link,
    innerLink,
    imageSize = ImageSize.Medium,
    subText,
    ninja,
    actionStatus = ActionStatus.Default,
    onClose,
    onLinkClick
}: Props) {
    let imageSrc = `img/${TEYZE_VARIANT_IMAGES[variant]}.png`;

    const isVampire = variant === Variant.Informative && isHalloween();
    if (isVampire) {
        imageSrc = "img/teyzeInformativeVampire.png";
    }

    function renderLink() {
        if (!link) {
            return null;
        }

        const { label, ...linkProps } = link;

        return (
            <Link
                class={classname("body-1 medium", styles.link)}
                onClick={onLinkClick}
                {...linkProps}>
                {label}
            </Link>
        );
    }

    function renderInnerLink() {
        if (!innerLink) {
            return null;
        }

        const { label, ...linkProps } = innerLink;

        return (
            <>
                {" "}
                <Link
                    class={classname("body-1 medium", styles.innerLink)}
                    onClick={onLinkClick}
                    {...linkProps}>
                    {label}
                </Link>
            </>
        );
    }

    function renderTeyzeContent() {
        let image = (
            <ResponsiveImage
                className={styles.image}
                asset={imageSrc}
                alt="Teyze"/>
        );

        if (actionStatus === ActionStatus.Loading) {
            image = (
                <CircleLoader/>
            );
        }

        if (actionStatus === ActionStatus.Completed) {
            image = (
                <div class={styles.actionCompleted}>
                    <IconCheck color="var(--label-primary-inverse)"/>
                </div>
            );
        }

        return (
            <div className={styles.teyzeContent}>
                <div className={classname(styles.imageBackground, styles[variant], {
                    [styles.small]: imageSize === "small",
                    [styles.vampire]: isVampire
                })}>
                    {image}
                </div>
                <div className={styles.teyzeContentBody}>
                    <span className={classname("body-1", styles.text)}>
                        {text}
                        {renderInnerLink()}
                        {textAfterInnerLink}
                    </span>
                    {renderLink()}
                    {subText}
                </div>
            </div>
        );
    }

    if (ninja) {
        return renderTeyzeContent();
    }

    return (
        <div className={classname(styles.teyze, cssClass)}>
            {renderTeyzeContent()}
            {onClose && (
                <div class={styles.teyzeCloseButton} onClick={onClose}>
                    <IconCloseSmall/>
                </div>
            )}
        </div>
    );
}

export default Teyze;
export {
    Props as TeyzeProps,
    TeyzeLinkProps,
    Variant as TeyzeVariant,
    ImageSize as TeyzeImageSize
};
